/**
 * Shared query/mutation failure classification for Sentry.
 *
 * log.error opens a Sentry issue. Cascade and client-transport failures
 * must not do that.
 */

// During login, queries can fire before the session token is fully valid,
// causing a cascade of expected failures. The root cause is already captured
// at the source — no need to duplicate these in the global error handler.
export const CASCADE_ERROR_PATTERN = /loading onboarding|loading tier|auto-create workspace|workspace|session expired|sign in again|not authenticated|auth-user-id|supabase is not defined|check-subscription|setup-workspace|seed-starter|list data|campaign enrollment/i;

/**
 * supabase-js throws this when the browser fetch rejects before the request
 * reaches an edge function (dropped connection, sleep/wake, blocked request).
 * NYNCH-DW / NYNCH-5N: QueryCache.onError was logging that as log.error, so
 * every exhausted retry became a Sentry exception. It is client transport
 * noise, not an application bug — the function never ran.
 */
export const TRANSIENT_EDGE_TRANSPORT_PATTERN =
  /failed to send a request to the edge function/i;

export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

/** Cascade / auth-hydration noise: do not log. */
export function isCascadeQueryError(message: string): boolean {
  return CASCADE_ERROR_PATTERN.test(message);
}

/** Fetch never left the browser. Breadcrumb only — do not open a Sentry issue. */
export function isTransientEdgeTransportError(message: string): boolean {
  return TRANSIENT_EDGE_TRANSPORT_PATTERN.test(message);
}

export function queryFailureLogLevel(error: unknown): 'error' | 'warn' | 'silent' {
  const message = extractErrorMessage(error);
  if (isCascadeQueryError(message)) return 'silent';
  if (isTransientEdgeTransportError(message)) return 'warn';
  return 'error';
}
